import { type FC } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AppRoutes, BankType, PortalType, ProductIds } from '@sparrow-card/sparrow-card-constants';
import { Logo } from 'assets/images';
import Link from 'components/Link';
import { ContentBlockIds } from 'enums/ContentBlockIds';
import { QueryParams } from 'enums/QueryParams';
import useDmBankPartner from 'hooks/useDmBankPartner';
import { type RootState } from 'stores';
import { CURRENT_YEAR, PRIVACY_LINK_PATH, TERMS_OF_USE_LINK_PATH } from './constants';
import styles from './Footer.module.scss';

const defaultText =
  'The Sparrow Card is issued by Evolve Bank and Trust, Member FDIC, pursuant to a license from Mastercard.';
const starlingText =
  'The Starling Card by Sparrow is issued by Focus Bank, Member FDIC, pursuant to a license from Mastercard.';
const focusText =
  'The Sparrow Card is issued by Focus Bank, Member FDIC, pursuant to a license from Mastercard.';
const fnblText =
  'The Sparrow Card is issued by First National Bank of Lacon, Member FDIC, pursuant to a license by Mastercard International.';
const directMailText =
  'Sparrow Financial, Inc. is a financial technology company, not a bank. Sparrow’s bank partners are Evolve Bank & Trust, Focus Bank, and the First National Bank of Lacon. The bank issuing your card will be identified in the Cardholder Agreement provided with your application. Sparrow cards are issued pursuant to a license from Mastercard International Incorporated.';

const LICENSE_TEXT_BY_BANK: Record<BankType, string> = {
  [BankType.Evolve]: defaultText,
  [BankType.Focus]: focusText,
  [BankType.Fnbl]: fnblText,
};

const Footer: FC = () => {
  const portalType = useSelector((state: RootState) => state.application.portalType);
  const [searchParams] = useSearchParams();
  const productId = searchParams.get(QueryParams.ProductID);
  const { pathname } = useLocation();
  const bankPartner = useDmBankPartner();

  const getLicenseText = () => {
    if (portalType === PortalType.DirectMail) {
      if (pathname === AppRoutes.PreApproved) {
        return directMailText;
      }

      return bankPartner ? LICENSE_TEXT_BY_BANK[bankPartner] : defaultText;
    }

    if (portalType === PortalType.Starling) {
      return starlingText;
    }

    if (productId === ProductIds.A04) {
      return fnblText;
    }

    return defaultText;
  };

  return (
    <footer className={styles.footer} id={ContentBlockIds.FooterContent}>
      <div className={styles.content}>
        <div className={styles.logo}>
          <Logo className={styles.logoIcon} role="presentation" />
          <span className="sr-only">Sparrow Card logo</span>
        </div>
        <div className={styles.footerInfo}>
          <p className={styles.license}>{getLicenseText()}</p>
          <p className={styles.copyright}>© {CURRENT_YEAR} Sparrow Financial Inc.</p>
          <p className={styles.copyright}>All Rights Reserved. This is a secure site.</p>
          <div className={styles.footerLinks}>
            <Link className={styles.footerLink} href={PRIVACY_LINK_PATH} target="_blank">
              Privacy
            </Link>
            <Link className={styles.footerLink} href={TERMS_OF_USE_LINK_PATH} target="_blank">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
